/**
 * The api's media refusals the composer answers in plain words (CT-15747, cs-analysis #466). Read by the body's
 * `code` with its status, never by the message's words; the CSP never counts files or mirrors the cap: the server
 * decides. Every other refusal keeps the line it had.
 *
 *   409 MEDIA_UNATTACHED_LIMIT  the upload (the multipart media route): this person already holds as many files
 *                               uploaded but not yet sent as the server allows. Each stops counting, and is deleted,
 *                               once it has waited about a day unsent (COMMS_MEDIA_UNATTACHED_SWEEP_AGE_MS); the
 *                               refusal does not carry that age, so it is said in words.
 *   409 MEDIA_UPLOAD_EXPIRED    a post or a ticket: the attached file waited unsent longer than the server keeps one,
 *                               nothing was sent. The composer keeps what was typed; the dead attachment is dropped.
 */

export const UPLOAD_LIMIT_WORDS =
    'Could not upload: too many of your files are waiting to be sent. Files you started but did not send are cleared automatically after about a day, so try again then.';

export const UPLOAD_EXPIRED_WORDS = 'Not sent: the file you attached waited too long and expired. Please upload it again; your message is still here.';

/** The body's `code` on a refusal with `status` (cs_analysis_fetch's and upload_error's `status` + `body`), else null. */
const refusal_code = (e, status) => {
    if (!e || e.status !== status || !e.body || typeof e.body !== 'object') return null;
    return typeof e.body.code === 'string' ? e.body.code : null;
};

/**
 * The upload's error for a non-2xx answer: the message as before ("upload 409"), with the status and the parsed body
 * (null when it is not JSON) beside it, as cs_analysis_fetch's errors carry them.
 */
export const upload_error = async (res) => {
    let body = null;
    try {
        body = await res.json();
    } catch (_) {
        body = null;
    }
    return Object.assign(new Error('upload ' + res.status), { status: res.status, body });
};

/** Whether an upload was refused for the upload limit. */
export const is_upload_limit = (e) => refusal_code(e, 409) === 'MEDIA_UNATTACHED_LIMIT';

/** What the representative is told when an upload fails: the plain line for the upload limit, else the line as before. */
export const upload_failure_words = (e) => (is_upload_limit(e) ? UPLOAD_LIMIT_WORDS : 'Could not upload: ' + ((e && e.message) || 'unknown error'));

/** Whether a post (or a ticket) was refused for an attachment that expired before it was sent. */
export const is_upload_expired = (e) => refusal_code(e, 409) === 'MEDIA_UPLOAD_EXPIRED';
